import { getAccessToken, unwrapList } from './apiUtils';
import { call } from './SalesService';
import { esc } from '../utils/documentTemplates';
import { getTerminalToken } from './TerminalService';
import { NoticeRow } from '../utils/orderNotifications';

/**
 * Aviso de recogida al cliente por correo. Usa el mismo /send_mail que la
 * factura (to, subject, content en HTML). Si el servidor rechaza el envio
 * lanza: quien llama no debe dar el aviso por hecho.
 */
const SEND_MAIL = '/send_mail';
const NOTICE_CREATE = '/order_notification_create';
const NOTICE_LIST = '/order_notification_list';

interface PickupNotice {
    to: string;
    clientName: string;
    orderNumber: string;
    /** Libros que ya se pueden recoger. */
    titles: string[];
    /** true = el pedido entero esta listo. */
    wholeOrder?: boolean;
}

const renderPickupHtml = (n: PickupNotice): string => {
    const books = n.titles.map(t => `<li>${esc(t)}</li>`).join('');
    const intro = n.wholeOrder
        ? `Su pedido <b>${esc(n.orderNumber)}</b> ya está completo y listo para recoger:`
        : `Ya puede recoger de su pedido <b>${esc(n.orderNumber)}</b>:`;
    return `<div style="font-family:Arial,sans-serif;font-size:15px;color:#1f2937">`
        + `<p>Hola ${esc(n.clientName)},</p>`
        + `<p>${intro}</p>`
        + `<ul>${books}</ul>`
        + `<p>Le esperamos en la librería.</p>`
        + `<p>Marcial Pons</p></div>`;
};

export const pickupSubject = (orderNumber: string): string =>
    `Su pedido ${orderNumber} está listo para recoger - Marcial Pons`;

export const sendPickupNotice = async (n: PickupNotice): Promise<void> => {
    const accessToken = getAccessToken();
    if (!accessToken) throw new Error('No autorizado');
    const to = n.to.trim();
    if (!to) throw new Error('Falta el email del cliente.');

    await call(SEND_MAIL, {
        access_token: accessToken,
        to,
        subject: pickupSubject(n.orderNumber),
        content: renderPickupHtml({ ...n, to })
    });
};

interface NoticeAttempt {
    orderId: string;
    /** Sin linea = aviso del pedido completo. */
    lineId?: string;
    recipient: string;
    subject: string;
    /** Motivo del fallo; vacio = el correo salio. */
    error?: string;
}

/** Guarda en el backend un intento de aviso, enviado o fallido. */
export const recordNotification = async (a: NoticeAttempt): Promise<void> => {
    const accessToken = getAccessToken();
    if (!accessToken) throw new Error('No autorizado');

    await call(NOTICE_CREATE, {
        access_token: accessToken,
        purchase_order_id: a.orderId,
        purchase_order_line_id: a.lineId,
        notification_type: a.lineId ? 'line' : 'order',
        recipient: a.recipient,
        subject: a.subject,
        status: a.error ? 'failed' : 'sent',
        error_message: a.error,
        terminal_token: getTerminalToken() || undefined
    });
};

/** Intentos de aviso de un pedido, del mas reciente al mas antiguo. */
export const listNotifications = async (orderId: string): Promise<NoticeRow[]> => {
    const accessToken = getAccessToken();
    if (!accessToken) throw new Error('No autorizado');

    const res = await call(NOTICE_LIST, { access_token: accessToken, purchase_order_id: orderId });
    return unwrapList(res).map((r: any): NoticeRow => ({
        lineId: r.purchase_order_line_id ? String(r.purchase_order_line_id) : '',
        type: String(r.notification_type || ''),
        recipient: String(r.recipient || ''),
        failed: r.status === 'failed',
        error: String(r.error_message || ''),
        createdAt: String(r.created_at || '')
    }));
};
